import { Component, Vue } from 'vue-property-decorator'
import { LoginParams } from '@/models/LoginModel'
import { ApiResponseLoginAuth } from '@/models/apiResponses/loginAuths'
import { UserRoles } from '@/const/UserRoles'
import { QrCodeSetting } from '@/const/ApiSettings'

/**
 * ログイン画面の共通処理をまとめたMixin用のコンポーネント
 */
@Component
export default class LoginMethods extends Vue {
  private errorMessages: string[] = []

  private loginApiCalled = false

  /**
   * 教室管理者ログイン
   */
  protected async branchLogin(accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      bcbtCode: accountCode,
      password: password,
      role: UserRoles.BRANCH,
    }
    await this.login(params)
  }

  /**
   * 教室管理者QRログイン
   */
  protected async branchQrLogin(qrCodeData: string): Promise<boolean> {
    const qrCodeParams = qrCodeData.split(QrCodeSetting.PARAM_SEPARATOR, 2)
    const params: LoginParams = {
      bcbtCode: qrCodeParams[0],
      password: qrCodeParams[1],
      role: UserRoles.BRANCH,
      isQrLogin: true,
    }
    return !!(await this.login(params))
  }

  /**
   * 塾管理者ログイン
   */
  protected async academyLogin(accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      bcbtCode: accountCode,
      password: password,
      role: UserRoles.ACADEMY,
    }
    await this.login(params)
  }

  /**
   * 塾管理者QRログイン
   */
  protected async academyQrLogin(qrCodeData: string): Promise<boolean> {
    const qrCodeParams = qrCodeData.split(QrCodeSetting.PARAM_SEPARATOR, 2)
    const params: LoginParams = {
      bcbtCode: qrCodeParams[0],
      password: qrCodeParams[1],
      role: UserRoles.ACADEMY,
      isQrLogin: true,
    }
    return !!(await this.login(params))
  }

  /**
   * 管理者ログイン
   */
  protected async adminLogin(bcbtCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      bcbtCode: bcbtCode,
      password: password,
      role: UserRoles.ADMIN,
    }
    await this.login(params)
  }

  /**
   * 生徒ログイン
   */
  protected async studentLogin(accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      bcbtCode: accountCode,
      password: password,
      role: UserRoles.STUDENT,
    }
    await this.login(params)
  }

  /**
   * 生徒QRログイン
   */
  protected async studentQrLogin(qrCodeData: string): Promise<boolean> {
    const qrCodeParams = qrCodeData.split(QrCodeSetting.PARAM_SEPARATOR, 2)
    const params: LoginParams = {
      bcbtCode: qrCodeParams[0],
      password: qrCodeParams[1],
      role: UserRoles.STUDENT,
      isQrLogin: true,
    }
    return !!(await this.login(params))
  }

  /**
   * ログイン処理 (Mixin先で呼び出す)
   *
   * このリクエストで、以下の一連の処理を実行
   * 認証リクエスト-Cookieへの保存 => Topページへの遷移
   */
  protected async login(params: LoginParams): Promise<boolean | undefined> {
    // 二重呼び出し防止
    if (this.loginApiCalled) return
    this.loginApiCalled = true

    const isLoginSuccess = await this.$http.http
      .post('/auth/sign_in', params)
      .then(async (res: ApiResponseLoginAuth): Promise<boolean> => {
        this.setCookieAuth(res)
        this.$bcbtCookies.studentCookie.remove()
        this.$bcbtCookies.adminCookie.remove()
        this.$bcbtCookies.academyCookie.remove()
        this.$bcbtCookies.branchCookie.remove()
        this.$router.push(this.topPagePath())
        return true
      })
      .catch(async (error: any): Promise<boolean> => {
        if (!error.response) {
          this.errorMessages = ['ネットワークエラーが発生しています。']
        } else if (error.response.data.errors) {
          this.errorMessages = error.response.data.errors
        } else {
          this.errorMessages = ['ログイン用の認証情報が正しくありません。', '再度お試しください。']
        }
        // 失敗時は再呼び出し可能に
        this.loginApiCalled = false
        return false
      })
    return isLoginSuccess
  }

  /**
   * ログアウト処理
   *
   * クッキーを削除する
   */
  protected logout() {
    this.$bcbtCookies.authBcbt.remove()
    this.$bcbtCookies.studentCookie.remove()
    this.$bcbtCookies.adminCookie.remove()
    this.$bcbtCookies.academyCookie.remove()
    this.$bcbtCookies.branchCookie.remove()
  }

  /**
   * ユーザ情報をCookieに保存
   * 内部メソッドの為、外部からの呼び出しは想定していない
   */
  private async setCookieAuth(res: ApiResponseLoginAuth) {
    const setParams = {
      accessToken: res.headers['access-token'],
      client: res.headers.client,
      uid: res.headers.uid,
      currentApiUserId: res.data.data.id,
      currentApiUserName: res.data.data.nickname,
      currentApiUserRole: res.data.data.role_name,
      currentApiBranchId: res.data.data.branch_id,
      currentApiBranchName: res.data.data.branch_name,
      currentApiAcademyId: res.data.data.academy_id,
      currentApiAcademyName: res.data.data.academy_name,
    }

    await this.$bcbtCookies.authBcbt.set(setParams)
  }

  /**
   * ログイン後の遷移先を取得
   * コンポーネント内でオーバーライドして指定する
   */
  public topPagePath(): string {
    // デフォルトではルートページを指定
    return '/'
  }
}
